import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty } from 'lodash-es'
import { AxiosError } from 'axios'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { createMfsShipperUpdateSchema, mcdShipperUpdateSchema, MFSShipperUpdateForm } from '@/services/shipper/schemas'
import { ShipperItem } from '@/services/shipper/types'
import { useUpdateMFSShipper } from '@/services/shipper/useShipperQueries'
import { useCompanyIdSpecialChars } from './useCompanyIdSpecialChars'

export function useMFSShipperUpdateForm(shipperData: ShipperItem, onRefresh: () => Promise<void>, onClose: () => void) {
  const updateDefaultValues: MFSShipperUpdateForm = {
    logisticsServiceType: 'MFS',
    shipperCode: shipperData.shipperCode,
    shipperName: shipperData.shipperName,
    companyName: shipperData.companyName,
    businessRegistrationNumber: shipperData.businessRegistrationNumber,
    ownerName: shipperData.ownerName,
    salesPlatforms: shipperData.salesPlatforms,
    businessType: shipperData.businessType || '',
    businessMajor: shipperData.businessMajor || '',
    zipCode: shipperData.zipCode || '',
    address: shipperData.address || '',
    phoneNumber: shipperData.phoneNumber || '',
    mfsTermsAgreedAt: shipperData.mfsTermsAgreedAt || '',
  }

  const updateMFSShipper = useUpdateMFSShipper()
  const allowSpecialChars = useCompanyIdSpecialChars()
  const schema = useMemo(() => createMfsShipperUpdateSchema(allowSpecialChars), [allowSpecialChars])

  const form = useForm<MFSShipperUpdateForm>({
    resolver: zodResolver(schema) as any,
    defaultValues: updateDefaultValues,
    mode: 'onChange',
  })

  const isValid = schema.safeParse(form.watch()).success && isEmpty(form.formState.errors)

  const handleSubmit = async () => {
    try {
      const formData = form.getValues()

      const { salesPlatforms, shipperName, ...transformedFormData } = formData

      await updateMFSShipper.mutateAsync({
        ...transformedFormData,
      })

      // MFS 수정 API 호출 (추후 구현)
      MToast.show('MFS 화주 수정이 완료되었습니다.')
      setTimeout(() => {
        onRefresh()
        onClose()
      }, 100)
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, 'MFS 화주 수정에 실패했습니다.'))
    }
  }

  return {
    form,
    isValid,
    handleSubmit,
  }
}
